"use client";

import { MediaProgressBar } from "@ui/components/core/MediaProgressBar";
import Orb from "@ui/components/demos/ConvAIHero/Orb/Orb";
import Book20 from "@ui/icons/20x20/book-20.svg";
import { cn } from "@ui/lib/utils";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";

import type { Segment } from "../types";

import { useSTTCurrentTime, useSTTMediaElement, useSTTVariant } from "../STTStore";
import { ORB_COLOURS } from "../orb/orbColors";

export default function TranscriptView({ className, segments }: { className?: string; segments: Segment[] }) {
  const mediaElement = useSTTMediaElement();
  const variant = useSTTVariant();

  const handleWordClickDefault = (startTime: number) => {
    if (!mediaElement) return;
    mediaElement.currentTime = startTime + 0.01;
    mediaElement.play();
  };

  return (
    <div className={cn("relative flex h-full flex-col", variant !== "simple" && "bg-default", className)}>
      <div className='relative flex-auto overflow-y-auto overflow-x-hidden'>
        <Transcription handleWordClick={handleWordClickDefault} segments={segments} />
      </div>
      <div>{variant !== "simple" && <MediaProgressBar mediaRef={{ current: mediaElement }} size='sm' />}</div>
    </div>
  );
}

const Transcription = ({
  segments = [],
  handleWordClick,
}: {
  handleWordClick: (time: number) => void;
  segments: Segment[];
}) => {
  const t = useTranslations("ClientKeys.SpeechToText");
  const currentTime = useSTTCurrentTime();
  const variant = useSTTVariant();

  if (!segments || segments.length === 0) {
    return (
      <div className='flex h-full flex-col items-center justify-center gap-8'>
        <m.div
          animate={{ scale: 1, opacity: 1 }}
          className='relative'
          initial={{ scale: 0.8, opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Book20 className='text-muted-foreground' />
        </m.div>
        <m.div
          animate={{ y: 0, opacity: 1 }}
          className='flex flex-col items-center gap-4'
          initial={{ y: 20, opacity: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <h3 className='f-ui-01'>{t("widget.no_transcript.title")}</h3>
          <p className='text-muted-foreground f-ui-02'>{t("widget.no_transcript.description")}</p>
        </m.div>
      </div>
    );
  }

  return (
    <div className='flex h-full flex-col'>
      {segments.map(segment => {
        const speakerId = parseInt(segment.speaker_id.replace("speaker_", ""), 10);

        return (
          <div className='mx-8 mb-20 mt-20 flex items-start' key={segment.id}>
            {variant === "default" && (
              <>
                <div className='sticky top-8 flex items-center justify-center rounded-full font-bold'>
                  <button className='h-28 w-28' onClick={() => handleWordClick(segment.start)}>
                    <Orb colors={ORB_COLOURS[speakerId % ORB_COLOURS.length]} />
                  </button>
                </div>
                <div className='ml-2 mr-5 w-[2px] self-stretch rounded-full bg-dark md:ml-16' />
              </>
            )}
            {variant === "simple" && (
              <div className='sticky top-8 pr-16'>
                <p className='f-paragraph-03 text-light'>{`${t("widget.speaker")} ${speakerId + 1}`}</p>
              </div>
            )}
            <div className='text-md f-paragraph-03 ml-5 mr-12 flex-1 whitespace-pre-wrap break-words leading-100'>
              {variant === "default" && (
                <div className='text-muted-foreground font-mono f-ui-03 text-xs'>
                  {`${t("widget.speaker")} ${speakerId + 1}`}
                </div>
              )}
              {segment.words.map(word => (
                <span
                  className={cn(
                    "group relative cursor-pointer text-sm transition-colors duration-300",
                    currentTime >= word.start ? "text-default" : "text-lightest",
                    "hover:text-default",
                  )}
                  key={word.id}
                  onClick={() => handleWordClick(word.end + 0.01)}
                >
                  {word.type == "spacing" && (
                    <div className='bg-gray-alpha-200 scale-80 absolute inset-0 inset-x-[-4px] inset-y-[-2px] rounded-md opacity-0 transition-all duration-100 group-hover:scale-100 group-hover:opacity-100' />
                  )}
                  {word.text}
                </span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
